'use strict';

import type { DeleteResponse } from '../CoreRepository';
import { CoreRepository } from '../CoreRepository';
import { CommissionStatementMapping } from '../../Entities/CommissionStatement/CommissionStatementMapping';
import { CommissionStatementMappingCollection } from '../../Collections/CommissionStatement/CommissionStatementMappingCollection';
import type { User } from '../../Entities/User/User';
import { Ind } from '../../Entities/Ind/Ind';
import { Utilities } from '../../Utilities/Utilities';
import { GlobCompanies } from '../../Entities/Glob/GlobCompanies';

/**
 * Enumeration of duplicate handling types for commission statement mappings
 */
export enum CommissionStatementMappingDuplicateType {
  /** Create multiple entries with each commission amount */
  Duplicate = 'duplicate',
  /** Merge commission amounts into a single entry */
  Merge = 'merge',
}

/**
 * Enumeration of available mapping field names
 * Defines all the field types that can be mapped from commission statement data
 */
export enum CommissionStatementMappingFieldName {
  // Beneficiary name fields
  BeneficiaryFullName = 'beneficiaryFullName',
  BeneficiaryFullNameReverse = 'beneficiaryFullNameReverse',
  BeneficiaryFirstName = 'beneficiaryFirstName',
  BeneficiaryLastName = 'beneficiaryLastName',

  // Beneficiary ID fields
  BeneficiaryMedicareID = 'beneficiaryMedicareID',
  BeneficiaryMedicaidID = 'beneficiaryMedicaidID',

  // Agent identification fields
  AgentNPN = 'agentNPN',
  AgentID = 'agentID',
  AgentFullName = 'agentFullName',
  AgentFullNameReverse = 'agentFullNameReverse',
  AgentFirstName = 'agentFirstName',
  AgentLastName = 'agentLastName',

  // Policy identification fields
  CompanyName = 'companyName',
  PolicyNumber = 'policyNumber',
  PolicyType = 'policyType',
  PolicyState = 'policyState',
  PolicyPlanName = 'policyPlanName',
  PolicyPBPID = 'policyPBPID',
  PolicyCMSContractNumber = 'policyCMSContractNumber',

  // Policy effective date fields (various formats)
  PolicyEffectiveDate = 'policyEffectiveDate',
  PolicyEffectiveDateYYYYMMDD = 'policyEffectiveDateYYYYMMDD',
  PolicyEffectiveDateYYYYDDMM = 'policyEffectiveDateYYYYDDMM',
  PolicyEffectiveDateYYYYMMDDHyphen = 'policyEffectiveDateYYYYMMDDHyphen',
  PolicyEffectiveDateDDMMYYYYHyphen = 'policyEffectiveDateDDMMYYYYHyphen',
  PolicyEffectiveDateDDMMYYHyphen = 'policyEffectiveDateDDMMYYHyphen',
  PolicyEffectiveDateMMDDYYYYHyphen = 'policyEffectiveDateMMDDYYYYHyphen',
  PolicyEffectiveDateMMDDYYHyphen = 'policyEffectiveDateMMDDYYHyphen',
  PolicyEffectiveDateYYYYMMDDSlash = 'policyEffectiveDateYYYYMMDDSlash',
  PolicyEffectiveDateDDMMYYYYSlash = 'policyEffectiveDateDDMMYYYYSlash',
  PolicyEffectiveDateMMDDYYYYSlash = 'policyEffectiveDateMMDDYYYYSlash',

  // Statement date fields (various formats)
  StatementDate = 'statementDate',
  StatementDateYYYYMMDD = 'statementDateYYYYMMDD',
  StatementDateYYYYDDMM = 'statementDateYYYYDDMM',
  StatementDateYYYYMMDDHyphen = 'statementDateYYYYMMDDHyphen',
  StatementDateDDMMYYYYHyphen = 'statementDateDDMMYYYYHyphen',
  StatementDateDDMMYYHyphen = 'statementDateDDMMYYHyphen',
  StatementDateMMDDYYYYHyphen = 'statementDateMMDDYYYYHyphen',
  StatementDateMMDDYYHyphen = 'statementDateMMDDYYHyphen',
  StatementDateYYYYMMDDSlash = 'statementDateYYYYMMDDSlash',
  StatementDateDDMMYYYYSlash = 'statementDateDDMMYYYYSlash',
  StatementDateMMDDYYYYSlash = 'statementDateMMDDYYYYSlash',

  // Commission fields
  CommissionPhase = 'commissionPhase',
  CommissionAmount = 'commissionAmount',
}

/**
 * Represents a single mapping field definition
 */
export type CommissionStatementMappingField = {
  /** The field name identifier */
  name: CommissionStatementMappingFieldName;
  /** Human-readable label for the field */
  label: string;
};

/**
 * Represents a single piece of mapping data linking a column to a field
 */
export type CommissionStatementMappingData = {
  /** The column number in the source data */
  column?: number;
  /** The field type this column maps to */
  field?: CommissionStatementMappingFieldName;
};

/**
 * Complex schema type used in the SDK for commission statement mapping operations
 * Contains full entity references and complex data types
 */
export interface CommissionStatementMappingSchema {
  /** Provider/company name for the mapping */
  providerName?: string;
  /** Mapping data defining field relationships */
  data?: any;
  /** Parent mapping entity reference */
  parent?: CommissionStatementMapping;
  /** Creation timestamp */
  dateTimeCreated?: Date;
  /** User entity reference */
  user?: User;
  /** How to handle duplicate entries */
  duplicateType?: CommissionStatementMappingDuplicateType;
  /** Whether the mapping is shared across users */
  isShared?: boolean;
  /** Row number containing headers */
  headerRow?: number;
  /** Array of row numbers to ignore during processing */
  ignoredRows?: Array<number> | null;
}

/**
 * Simple payload type for API communication
 * Contains primitive values and IDs instead of entity references
 */
export interface CommissionStatementMappingPayload {
  /** Provider/company name for the mapping */
  providerName?: string;
  /** Mapping data defining field relationships */
  data?: any;
  /** Parent mapping ID */
  parent?: number;
  /** Creation timestamp as string */
  dateTimeCreated?: string;
  /** User ID */
  user?: number;
  /** Whether the mapping is shared across users */
  isShared?: boolean;
  /** Row number containing headers */
  headerRow?: number;
  /** Array of row numbers to ignore during processing */
  ignoredRows?: Array<number> | null;
  /** How to handle duplicate entries */
  duplicateType?: CommissionStatementMappingDuplicateType;
}

/**
 * API response format for commission statement mapping collections
 */
export interface CommissionStatementMappingCollectionPayload {
  /** Maximum number of results per page */
  limit?: number;
  /** Total number of available results */
  total?: number;
  /** Offset for pagination */
  offset?: number;
  /** Array of commission statement mapping data */
  data?: Array<CommissionStatementMappingPayload>;
}

/**
 * Commission Statement Mapping Repository
 *
 * Repository class for CRUD operations and field management within the context
 * of CommissionStatementMapping entities. Provides validation, field definitions,
 * and API communication for commission statement mapping operations.
 *
 * @author Matt Lowden <matt.lowden@medicareprocrm.com>
 */
export class CommissionStatementMappingRepository extends CoreRepository {
  /**
   * Validates that required fields are present in a mapping configuration
   *
   * Ensures that essential fields for beneficiary identification, policy information,
   * commission data, and agent information are properly mapped before processing.
   *
   * @param fields - Array of mapping fields to validate
   * @returns True if validation passes
   * @throws Error with specific validation failure message
   */
  public validateFields(fields: Array<CommissionStatementMappingData>): boolean {
    if (!fields) {
      throw new Error('Please map your fields below');
    }

    // Validate beneficiary name fields
    const beneficiaryFullName =
      fields.find(
        (field) =>
          field.field === CommissionStatementMappingFieldName.BeneficiaryFullName ||
          field.field === CommissionStatementMappingFieldName.BeneficiaryFullNameReverse
      ) !== undefined;

    const beneficiaryFirstName =
      fields.find(
        (field) => field.field === CommissionStatementMappingFieldName.BeneficiaryFirstName
      ) !== undefined;

    const beneficiaryLastName =
      fields.find(
        (field) => field.field === CommissionStatementMappingFieldName.BeneficiaryLastName
      ) !== undefined;

    if (!beneficiaryFullName && !(beneficiaryFirstName && beneficiaryLastName)) {
      throw new Error(
        'beneficiaryFullName or beneficiaryFirstName & beneficiaryLastName must be present'
      );
    }

    // Validate commission amount field
    const commissionAmount =
      fields.find(
        (field) => field.field === CommissionStatementMappingFieldName.CommissionAmount
      ) !== undefined;

    if (!commissionAmount) {
      throw new Error('Commission Amount must be present');
    }

    // Validate policy identification fields
    const policyNumber =
      fields.find((field) => field.field === CommissionStatementMappingFieldName.PolicyNumber) !==
      undefined;

    const policyType =
      fields.find((field) => field.field === CommissionStatementMappingFieldName.PolicyType) !==
      undefined;

    const policyEffectiveDate =
      fields.find((field) =>
        [
          CommissionStatementMappingFieldName.PolicyEffectiveDate,
          CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYMMDD,
          CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYDDMM,
          CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYMMDDHyphen,
          CommissionStatementMappingFieldName.PolicyEffectiveDateDDMMYYYYHyphen,
          CommissionStatementMappingFieldName.PolicyEffectiveDateDDMMYYHyphen,
          CommissionStatementMappingFieldName.PolicyEffectiveDateMMDDYYYYHyphen,
          CommissionStatementMappingFieldName.PolicyEffectiveDateMMDDYYHyphen,
          CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYMMDDSlash,
          CommissionStatementMappingFieldName.PolicyEffectiveDateDDMMYYYYSlash,
          CommissionStatementMappingFieldName.PolicyEffectiveDateMMDDYYYYSlash,
        ].includes(field.field)
      ) !== undefined;

    const policyPlanName =
      fields.find((field) => field.field === CommissionStatementMappingFieldName.PolicyPlanName) !==
      undefined;

    if (
      !policyNumber &&
      !(policyEffectiveDate && policyType) &&
      !(policyEffectiveDate && policyPlanName)
    ) {
      throw new Error(
        'PolicyNumber or Policy Effective Date and Policy Type or Policy Effective Date and Policy Plan Name must be present'
      );
    }

    // Validate agent identification fields
    const agentFullName =
      fields.find(
        (field) =>
          field.field === CommissionStatementMappingFieldName.AgentFullName ||
          field.field === CommissionStatementMappingFieldName.AgentFullNameReverse
      ) !== undefined;

    const agentFirstName =
      fields.find((field) => field.field === CommissionStatementMappingFieldName.AgentFirstName) !==
      undefined;

    const agentLastName =
      fields.find((field) => field.field === CommissionStatementMappingFieldName.AgentLastName) !==
      undefined;

    if (!agentFullName && !(agentFirstName && agentLastName)) {
      throw new Error('Agent Full Name or Agent First Name & Agent Last Name must be present');
    }

    return true;
  }

  /**
   * Gets the complete list of available mapping fields
   *
   * Returns all possible field types that can be mapped from commission statement
   * data, including various date formats, name formats, and identification fields.
   *
   * @returns Array of mapping field definitions with names and labels
   */
  public getFields(): Array<CommissionStatementMappingField> {
    const fields: Array<CommissionStatementMappingField> = [
      // Beneficiary name fields
      {
        name: CommissionStatementMappingFieldName.BeneficiaryFullName,
        label: 'Beneficiary Full Name',
      },
      {
        name: CommissionStatementMappingFieldName.BeneficiaryFullNameReverse,
        label: 'Beneficiary Full Name (Last name, First name)',
      },
      {
        name: CommissionStatementMappingFieldName.BeneficiaryFirstName,
        label: 'Beneficiary First Name',
      },
      {
        name: CommissionStatementMappingFieldName.BeneficiaryLastName,
        label: 'Beneficiary Last Name',
      },
      {
        name: CommissionStatementMappingFieldName.BeneficiaryMedicareID,
        label: 'Beneficiary Medicare ID',
      },
      {
        name: CommissionStatementMappingFieldName.BeneficiaryMedicaidID,
        label: 'Beneficiary Medicaid ID',
      },
      // Agent identification fields
      {
        name: CommissionStatementMappingFieldName.AgentNPN,
        label: 'Agent NPN',
      },
      {
        name: CommissionStatementMappingFieldName.AgentID,
        label: 'Agent ID',
      },
      {
        name: CommissionStatementMappingFieldName.AgentFullName,
        label: 'Agent Full Name',
      },
      {
        name: CommissionStatementMappingFieldName.AgentFullNameReverse,
        label: 'Agent Full Name (Last name, First name)',
      },
      {
        name: CommissionStatementMappingFieldName.AgentFirstName,
        label: 'Agent First Name',
      },
      {
        name: CommissionStatementMappingFieldName.AgentLastName,
        label: 'Agent Last Name',
      },
      // Policy identification fields
      {
        name: CommissionStatementMappingFieldName.CompanyName,
        label: 'Policy Company Name',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyNumber,
        label: 'Policy Number / Member ID',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyCMSContractNumber,
        label: 'Policy CMS Contract Number',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyPBPID,
        label: 'Policy PBP ID',
      },
      // Policy date fields (various formats)
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDate,
        label: 'Policy Effective Date (Number)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYMMDD,
        label: 'Policy Effective Date (YYYYMMDD)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYDDMM,
        label: 'Policy Effective Date (YYYYDDMM)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYMMDDHyphen,
        label: 'Policy Effective Date (YYYY-MM-DD)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateDDMMYYYYHyphen,
        label: 'Policy Effective Date (DD-MM-YYYY)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateDDMMYYHyphen,
        label: 'Policy Effective Date (DD-MM-YY)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateMMDDYYYYHyphen,
        label: 'Policy Effective Date (MM-DD-YYYY)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateMMDDYYHyphen,
        label: 'Policy Effective Date (MM-DD-YY)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateYYYYMMDDSlash,
        label: 'Policy Effective Date (YYYY/MM/DD)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateDDMMYYYYSlash,
        label: 'Policy Effective Date (DD/MM/YYYY)',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyEffectiveDateMMDDYYYYSlash,
        label: 'Policy Effective Date (MM/DD/YYYY)',
      },
      // Policy details fields
      {
        name: CommissionStatementMappingFieldName.PolicyState,
        label: 'Policy State',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyType,
        label: 'Policy Type',
      },
      {
        name: CommissionStatementMappingFieldName.PolicyPlanName,
        label: 'Policy Plan Name',
      },
      // Commission fields
      {
        name: CommissionStatementMappingFieldName.CommissionPhase,
        label: 'Commission Phase',
      },
      {
        name: CommissionStatementMappingFieldName.CommissionAmount,
        label: 'Commission Amount',
      },
      // Statement date fields (various formats)
      {
        name: CommissionStatementMappingFieldName.StatementDate,
        label: 'Statement Date (Number)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateYYYYMMDD,
        label: 'Statement Date (YYYYMMDD)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateYYYYDDMM,
        label: 'Statement Date (YYYYDDMM)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateYYYYMMDDHyphen,
        label: 'Statement Date (YYYY-MM-DD)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateDDMMYYYYHyphen,
        label: 'Statement Date (DD-MM-YYYY)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateDDMMYYHyphen,
        label: 'Statement Date (DD-MM-YY)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateMMDDYYYYHyphen,
        label: 'Statement Date (MM-DD-YYYY)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateMMDDYYHyphen,
        label: 'Statement Date (MM-DD-YY)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateYYYYMMDDSlash,
        label: 'Statement Date (YYYY/MM/DD)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateDDMMYYYYSlash,
        label: 'Statement Date (DD/MM/YYYY)',
      },
      {
        name: CommissionStatementMappingFieldName.StatementDateMMDDYYYYSlash,
        label: 'Statement Date (MM/DD/YYYY)',
      },
    ];

    return fields;
  }

  /**
   * Converts complex schema data to simplified payload format for API communication
   *
   * Transforms CommissionStatementMappingSchema objects (which contain entity references)
   * into CommissionStatementMappingPayload objects (which contain primitive values and IDs)
   * suitable for API transmission.
   *
   * @param data - Complex schema data to be converted
   * @returns Simplified payload data structure for API usage
   */
  protected processData(data: CommissionStatementMappingSchema): CommissionStatementMappingPayload {
    const payload: CommissionStatementMappingPayload = {};

    // Copy primitive properties directly
    if (data.hasOwnProperty('providerName')) {
      payload.providerName = data.providerName;
    }

    if (data.hasOwnProperty('data')) {
      payload.data = data.data;
    }

    // Convert entity references to IDs
    if (data.user) {
      payload.user = data.user.getUserId();
    }

    if (data.parent) {
      payload.parent = data.parent.getId();
    }

    // Copy boolean and enum properties
    if (data.hasOwnProperty('isShared')) {
      payload.isShared = data.isShared;
    }

    if (data.hasOwnProperty('duplicateType')) {
      payload.duplicateType = data.duplicateType;
    }

    if (data.hasOwnProperty('headerRow')) {
      payload.headerRow = data.headerRow;
    }

    if (data.hasOwnProperty('ignoredRows')) {
      payload.ignoredRows = data.ignoredRows;
    }

    // Convert Date objects to string format
    if (data.hasOwnProperty('dateTimeCreated')) {
      payload.dateTimeCreated = data.dateTimeCreated
        ? Utilities.dateTimeToDateTimeString(data.dateTimeCreated)
        : null;
    }

    return payload;
  }

  /**
   * Retrieves a collection of commission statement mappings for a user
   *
   * Fetches commission statement mappings from the API and converts them into
   * a properly hydrated CommissionStatementMappingCollection with pagination metadata.
   *
   * @param userId - ID of the user whose mappings are being requested
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise resolving to a collection of commission statement mappings
   */
  public getCommissionStatementMappings(
    userId: number,
    options = {}
  ): Promise<CommissionStatementMappingCollection> {
    return this.webAPI.getCommissionStatementMappings(userId, options).then((response) => {
      const collection = new CommissionStatementMappingCollection();

      // Hydrate each mapping entity from the API response
      response.data.forEach((collectionItemData) => {
        const collectionItem = new CommissionStatementMapping();
        collectionItem.hydrate(collectionItemData);

        collection.addItem(collectionItem);
      });

      // Set pagination metadata
      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }

  /**
   * Retrieves a single commission statement mapping by ID
   *
   * @param userId - ID of the user who owns the mapping
   * @param commissionStatementMappingId - ID of the specific mapping to retrieve
   * @param options - Additional query parameters
   * @returns Promise resolving to the requested commission statement mapping
   * @throws Exception if no matching mapping exists or user isn't authenticated
   */
  public getCommissionStatementMapping(
    userId: number,
    commissionStatementMappingId: number,
    options = {}
  ): Promise<CommissionStatementMapping> {
    return this.webAPI
      .getCommissionStatementMapping(userId, commissionStatementMappingId, options)
      .then((response) => {
        const item = new CommissionStatementMapping();
        item.hydrate(response);

        return item;
      });
  }

  /**
   * Updates an existing commission statement mapping
   *
   * @param userId - ID of the user who owns the mapping
   * @param commissionStatementMappingId - ID of the mapping to update
   * @param data - Updated mapping data in schema format
   * @returns Promise resolving to the updated commission statement mapping
   * @throws Exception if mapping doesn't exist, user isn't authenticated, or data is invalid
   */
  public updateCommissionStatementMapping(
    userId: number,
    commissionStatementMappingId: number,
    data: CommissionStatementMappingSchema
  ): Promise<CommissionStatementMapping> {
    const payload = this.processData(data);

    return this.webAPI
      .updateCommissionStatementMapping(userId, commissionStatementMappingId, payload)
      .then((response) => {
        const item = new CommissionStatementMapping();
        item.hydrate(response);

        return item;
      });
  }

  /**
   * Creates a new commission statement mapping
   *
   * @param userId - ID of the user who will own the mapping
   * @param data - Mapping data in schema format
   * @returns Promise resolving to the created commission statement mapping
   * @throws Exception if user isn't authenticated or supplied data is invalid
   */
  public createCommissionStatementMapping(
    userId: number,
    data: CommissionStatementMappingSchema
  ): Promise<CommissionStatementMapping> {
    const payload = this.processData(data);

    return this.webAPI.createCommissionStatementMapping(userId, payload).then((response) => {
      const item = new CommissionStatementMapping();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Deletes a commission statement mapping
   *
   * @param userId - ID of the user who owns the mapping
   * @param commissionStatementMappingId - ID of the mapping to delete
   * @returns Promise resolving to deletion response
   * @throws Exception if user isn't authenticated or mapping doesn't exist
   */
  public deleteCommissionStatementMapping(
    userId: number,
    commissionStatementMappingId: number
  ): Promise<DeleteResponse> {
    return this.webAPI
      .deleteCommissionStatementMapping(userId, commissionStatementMappingId)
      .then((response) => {
        return response;
      });
  }
}
